// src/pages/admin/FundingBonusSettings.jsx
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import { Gift, Save, RefreshCw, Plus, Trash2, Info, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";

const MAX_TIERS = 6;
const naira = (n) => "₦" + Math.round(Number(n) || 0).toLocaleString("en-NG");

function previewBonus(amount, tiers, maxBonus) {
  let best = null;
  for (const t of tiers) {
    if (amount >= t.minAmount && (!best || t.minAmount > best.minAmount)) best = t;
  }
  if (!best) return { bonus: 0, percent: 0 };
  let bonus = Math.floor((amount * best.percent) / 100);
  const cap = Number(maxBonus) || 0;
  if (cap > 0 && bonus > cap) bonus = cap;
  return { bonus, percent: best.percent };
}

export default function FundingBonusSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [maxBonus, setMaxBonus] = useState("0");
  const [testAmount, setTestAmount] = useState("100000");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/funding-bonus");
      if (res.data.success) {
        const s = res.data.settings;
        setEnabled(!!s.enabled);
        setTiers(
          (s.tiers || []).map((t) => ({ minAmount: String(t.minAmount), percent: String(t.percent) }))
        );
        setMaxBonus(String(s.maxBonus || 0));
      }
    } catch (err) {
      toast.error("Failed to load funding bonus settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const parsedTiers = tiers
    .map((t) => ({ minAmount: Number(t.minAmount), percent: Number(t.percent) }))
    .filter((t) => t.minAmount > 0 && t.percent > 0 && t.percent <= 50)
    .sort((a, b) => a.minAmount - b.minAmount);

  const validate = () => {
    if (enabled && tiers.length === 0) return "Add at least one tier, or switch the bonus off";
    const seen = new Set();
    for (const t of tiers) {
      const min = Number(t.minAmount);
      const pct = Number(t.percent);
      if (!(min > 0)) return "Each tier needs a deposit amount above 0";
      if (!(pct > 0) || pct > 50) return "Each bonus percentage must be above 0 and at most 50";
      if (seen.has(min)) return "Two tiers have the same deposit amount";
      seen.add(min);
    }
    const cap = Number(maxBonus);
    if (isNaN(cap) || cap < 0) return "Maximum bonus must be 0 (no limit) or a positive amount";
    return "";
  };

  const updateTier = (index, field, value) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const addTier = () => {
    if (tiers.length >= MAX_TIERS) return;
    setTiers((prev) => [...prev, { minAmount: "", percent: "" }]);
  };

  const removeTier = (index) => {
    setTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      await adminApi.put("/funding-bonus", {
        enabled,
        maxBonus: Number(maxBonus) || 0,
        tiers: tiers.map((t) => ({ minAmount: Number(t.minAmount), percent: Number(t.percent) })),
      });
      toast.success("Funding bonus settings saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save funding bonus settings");
    } finally {
      setSaving(false);
    }
  };

  const test = previewBonus(Number(testAmount) || 0, parsedTiers, maxBonus);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-amber-200 dark:border-amber-900 p-6 flex items-center gap-3">
        <RefreshCw size={16} className="text-amber-500 animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading funding bonus...</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-amber-200 dark:border-amber-900 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-amber-100 dark:border-amber-900">
        <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
          <Gift size={15} className="text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Funding Bonus</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Reward users with a percentage bonus on large single deposits
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((v) => !v)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${
            enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-700"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      <div className="p-5 space-y-5">
        <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
          <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
            The bonus applies to a single deposit and is added to the wallet automatically. If a deposit
            qualifies for several tiers, the highest one is used. The bonus is currently{" "}
            <strong>{enabled ? "ON" : "OFF"}</strong>. Remember to press Save after any change.
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Bonus tiers</p>
          <div className="space-y-2">
            {tiers.length === 0 && (
              <p className="text-xs text-gray-400">No tiers yet. Add one below.</p>
            )}
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Deposit of at least (₦)</label>
                  <input
                    type="number"
                    min="1"
                    value={t.minAmount}
                    onChange={(e) => updateTier(i, "minAmount", e.target.value)}
                    placeholder="50000"
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:text-white font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Bonus (%)</label>
                  <input
                    type="number"
                    min="0.1"
                    max="50"
                    step="0.5"
                    value={t.percent}
                    onChange={(e) => updateTier(i, "percent", e.target.value)}
                    placeholder="5"
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:text-white font-mono"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeTier(i)}
                  aria-label="Remove tier"
                  className="p-2.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addTier}
            disabled={tiers.length >= MAX_TIERS}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50 transition"
          >
            <Plus size={13} /> Add tier
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Maximum bonus per deposit (₦)
          </label>
          <input
            type="number"
            min="0"
            value={maxBonus}
            onChange={(e) => setMaxBonus(e.target.value)}
            className="w-48 px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:text-white font-mono"
          />
          <p className="text-[11px] text-gray-400 mt-1">Enter 0 for no limit.</p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Preview</p>
          {parsedTiers.length === 0 ? (
            <p className="text-xs text-gray-400">Add a valid tier to see a preview.</p>
          ) : (
            <div className="space-y-1.5">
              {parsedTiers.map((t) => {
                const r = previewBonus(t.minAmount, parsedTiers, maxBonus);
                return (
                  <div key={t.minAmount} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">
                      Deposit {naira(t.minAmount)}
                    </span>
                    <span className="font-semibold text-amber-600 dark:text-amber-400">
                      +{naira(r.bonus)} ({r.percent}%) = {naira(t.minAmount + r.bonus)} in wallet
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-500 dark:text-gray-400">Try an amount: ₦</span>
            <input
              type="number"
              min="0"
              value={testAmount}
              onChange={(e) => setTestAmount(e.target.value)}
              className="w-32 px-2 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:text-white font-mono"
            />
            <span className="font-semibold text-gray-800 dark:text-gray-200">
              {test.bonus > 0
                ? "bonus " + naira(test.bonus) + " (" + test.percent + "%)"
                : "no bonus"}
            </span>
          </div>
        </div>

        {validate() && (
          <p className="flex items-center gap-1 text-xs text-red-500">
            <AlertTriangle size={11} /> {validate()}
          </p>
        )}

        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition"
        >
          {saving ? (
            <>
              <RefreshCw size={14} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={14} />
              Save Funding Bonus
            </>
          )}
        </button>
      </div>
    </div>
  );
}